import Layout from "@/components/application/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import useCulqiCheckout from "@/hooks/use-culqi-checkout";
import { post } from "@/lib/api";
import { AdminBillingPageProps, BillingPlan } from "@/types";
import { router, usePage } from "@inertiajs/react";
import { ReactNode, useState } from "react";
import useTranslations from "@/hooks/use-translations";

function BillingPage() {
  const {
    current_user,
    current_plan,
    subscription,
    available_plans,
    culqi_public_key,
  } = usePage<AdminBillingPageProps>().props;
  const { t } = useTranslations("site.billing");
  const openCheckout = useCulqiCheckout();
  const [subscribingPlanId, setSubscribingPlanId] = useState<number | null>(
    null
  );
  const [canceling, setCanceling] = useState(false);

  async function subscribe(plan: BillingPlan) {
    setSubscribingPlanId(plan.id);

    try {
      const tokenId = await openCheckout({
        publicKey: culqi_public_key,
        amountCents: plan.amount_cents,
        currency: plan.currency.toUpperCase(),
        title: "BrickGestor",
        description: `Subscription to ${plan.name}`,
        email: current_user!.email,
      });

      await post("billing/subscribe", { plan_id: plan.id, token_id: tokenId });

      router.reload();
    } finally {
      setSubscribingPlanId(null);
    }
  }

  async function cancelSubscription() {
    if (!window.confirm(t("cancel_confirm"))) {
      return;
    }

    setCanceling(true);

    try {
      await post("billing/cancel");
      router.reload();
    } finally {
      setCanceling(false);
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <h2 className="text-2xl font-semibold">{t("title")}</h2>

      {current_plan && (
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>{t("current_plan")}</CardTitle>
            <CardDescription>
              {current_plan
                ? t("on_plan", { plan: current_plan })
                : t("no_subscription")}
            </CardDescription>
            {subscription && (
              <CardAction>
                <Badge
                  variant={
                    subscription.status === "active" ? "default" : "secondary"
                  }
                >
                  {subscription.status}
                </Badge>
              </CardAction>
            )}
          </CardHeader>

          {subscription && (
            <CardContent className="flex flex-col gap-2 text-sm text-muted-foreground">
              {subscription.card_brand && subscription.card_last_four && (
                <p>
                  {t("card_ending_in", {
                    brand: subscription.card_brand,
                    last_four: subscription.card_last_four,
                  })}
                </p>
              )}
              {subscription.renews_at && (
                <p>
                  {t("renews_at", {
                    date: new Date(
                      subscription.renews_at
                    ).toLocaleDateString(),
                  })}
                </p>
              )}

              <div>
                <Button
                  variant="destructive"
                  onClick={cancelSubscription}
                  disabled={canceling}
                >
                  {canceling && <Spinner />}
                  {t("cancel_subscription")}
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {!subscription && (
        <div className="grid gap-4 md:grid-cols-2 max-w-2xl">
          {available_plans.map((plan) => (
            <Card key={plan.id}>
              <CardHeader>
                <CardTitle>{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <p className="text-xl font-semibold">{plan.price_formatted}</p>
                <Button
                  onClick={() => subscribe(plan)}
                  disabled={subscribingPlanId !== null}
                >
                  {subscribingPlanId === plan.id && <Spinner />}
                  {t("subscribe")}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

BillingPage.layout = (page: ReactNode) => <Layout children={page} />;

export default BillingPage;
